import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipContainer } from '../../UI';

interface DerivedFromIncomeSourceTooltipProps {
  derived: boolean;
  children: ReactNode;
}

/**
 * Shows a "calculated from the linked income source" hint on hover over the (disabled) amount
 * and frequency inputs. Deliberately a pure-CSS group-hover tooltip — the floating-ui-based
 * Tooltip (`useHover` + `whileElementsMounted: autoUpdate`) sent the form into an infinite
 * re-render loop when it wrapped these self-updating disabled dropdowns (ELE-850). No state or
 * effects here, so a render loop is structurally impossible.
 *
 * The wrapper element is rendered unconditionally (only the hint bubble is gated on `derived`).
 * Branching the element *type* on `derived` (e.g. `<>{children}</>` vs `<div>{children}…</div>`)
 * changes the parent of `children` when `derived` flips, forcing React to unmount and remount the
 * whole subtree. For the self-updating amount/frequency dropdowns that remount re-fires their
 * `onChange`, writing back into the form and flipping `derived` again — an infinite remount loop
 * (ELE-850). Keeping one stable wrapper node makes toggling `derived` a cheap, in-place update.
 */
export const DerivedFromIncomeSourceTooltip = ({ derived, children }: DerivedFromIncomeSourceTooltipProps) => {
  const { t: tForm } = useTranslation('form');

  return (
    <div className="group relative">
      {children}
      {derived && (
        <>
          {/* The hover bubble below is `hidden` until :hover, which keeps it out of the
              accessibility tree entirely — and the control it explains is disabled, so it can't be
              focused to reveal it either. This copy is always in the tree for screen readers.
              Static markup, so it can't reintroduce the ELE-850 render loop. */}
          <span className="sr-only">
            {tForm('investment-recurring-contribution-derived-from-income-source-tooltip')}
          </span>
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-full right-0 z-50 mb-2 hidden w-max max-w-[284px] group-hover:block"
          >
            <TooltipContainer>
              <span className="text-sm text-white">
                {tForm('investment-recurring-contribution-derived-from-income-source-tooltip')}
              </span>
            </TooltipContainer>
          </div>
        </>
      )}
    </div>
  );
};
